import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import {appendQueryStringTemplate} from '@/hooks/externalLink/externalLinkQueryUtils';
import iccSsoService from '@/services/icc/iccSsoService';
import {resolveSsoLinkUrlForOpenFromState} from '@/services/sso/ssoIccSessionInfoPolicy.wired';
import userBrowserService from '@/services/user/userBrowserService';
import {makeSelectSsoLink} from '@/state/selectors/media';

import useSsoLinkRefresh from './useSsoLinkRefresh';

export type UseSsoLinkSingleParams = {
    ssoTemplate?: string;
    ssoParams?: Record<string, unknown>;
    openInBluefy?: boolean;
    linkProcessor?: (url: string) => string;
    disabled?: boolean;
    queryStringTemplate?: string;
};

export type UseSsoLinkSingleResult = {
    /** Resolved SSO URL with optional query string template applied. */
    link: string | undefined;
    /** When true, open SSO URL in a new tab instead of the unified external-link flow. */
    preferSsoLinkInNewTab: boolean;
};

/**
 * Single CreateLoginToken SSO path: Redux cache, refresh on expiry, fetch via getSsoLink.
 * Pass disabled when there is no ssoTemplate (Rules of Hooks) or the parent disables the flow.
 */
const useSsoLinkSingle = ({
    ssoTemplate,
    ssoParams,
    openInBluefy,
    linkProcessor,
    disabled,
    queryStringTemplate,
}: UseSsoLinkSingleParams): UseSsoLinkSingleResult => {
    const formattedSsoTemplate = iccSsoService.getFormattedSsoTemplate({ssoTemplate, params: ssoParams});
    const isIos = userBrowserService.isIosDevice();
    const ssoEntry = useSelector(makeSelectSsoLink(formattedSsoTemplate));
    const {status, expirationDate} = ssoEntry;
    const resolved = resolveSsoLinkUrlForOpenFromState(ssoEntry, Boolean(openInBluefy));
    const processed = resolved && linkProcessor ? linkProcessor(resolved) : resolved;
    const linkWithQuery = appendQueryStringTemplate(processed, queryStringTemplate);

    useSsoLinkFetch({ssoTemplate, ssoParams, status, linkProcessor, disabled});
    useSsoLinkRefresh({expirationDate, formattedSsoTemplate, disabled});

    const preferSsoLinkInNewTab = Boolean(ssoTemplate && isIos && !openInBluefy && processed);

    return {
        link: linkWithQuery,
        preferSsoLinkInNewTab,
    };
};

const useSsoLinkFetch = ({
    ssoTemplate,
    ssoParams,
    status,
    linkProcessor,
    disabled,
}: {
    ssoTemplate?: string;
    ssoParams?: Record<string, unknown>;
    status?: unknown;
    linkProcessor?: (url: string) => string;
    disabled?: boolean;
}) => {
    useEffect(() => {
        if (disabled) return;
        if (status) return;

        iccSsoService.getSsoLink({
            ssoTemplate,
            params: ssoParams,
            linkProcessor,
        });
    }, [ssoTemplate, status, disabled]);
};

export default useSsoLinkSingle;
